import { useMemo } from 'react';
import type { FeatureCollection, Feature, Point, LineString } from 'geojson';
import type { components } from '@/types/api.generated';
import type { RoutePlanningStop } from '../map/RoutePlanningMap';
import { lookupEnumLabelOrWarn } from '@lib/enum-label';
import { jobStatusOptions } from '../jobResource';

type DispatchRouteEtaReport = components['schemas']['DispatchRouteEtaReport'];
type EtaDriver = DispatchRouteEtaReport['drivers'][number];

export const DRIVER_COLORS = [
  '#2563EB', // Blue
  '#D97706', // Amber
  '#059669', // Emerald
  '#7C3AED', // Violet
  '#DC2626', // Red
  '#0891B2', // Cyan
  '#DB2777', // Pink
  '#4F46E5', // Indigo
  '#65A30D', // Lime
  '#EA580C', // Orange
  '#0D9488', // Teal
  '#9333EA', // Purple
];

export const UNASSIGNED_COLOR = '#9ca3af';

export interface MappedDriverTask {
  taskId: string;
  orderName: string | null;
  state: 'assigned' | 'active' | 'completed';
  estimatedCompletionTime: number | null;
  completedAt: number | null;
  /**
   * Every stop this one Onfleet task delivers. `fulfillment_jobs.onfleet_task_id`
   * is deliberately non-unique (ADR-0026 D2) — with consolidation on, one task
   * carries N same-address same-day delivery jobs — so a single-stop field
   * would drop N-1 of them. Empty means the task reached no stop at all.
   */
  stops: RoutePlanningStop[];
}

export interface MappedDriverRoute {
  workerId: string;
  workerName: string;
  color: string;
  location: [number, number] | null;
  onDuty: boolean;
  timeLastSeen: number | null;
  tasks: MappedDriverTask[];
  completedCount: number;
  coordinates: [number, number][];
}

export interface EnrichedStopProperties {
  job_id: string;
  order_name: string;
  zip: string;
  status: RoutePlanningStop['status'];
  status_label: string;
  driver_id: string | null;
  driver_name: string | null;
  driver_color: string;
  eta: number | null;
  completed_at: number | null;
  /**
   * What the live board says about this stop. A live task contributes its own
   * state; `null` means the board answered and no task claims the stop — the
   * grey "no driver" dot. `'unknown'` is not a task state at all: it means the
   * board has not answered yet (the ETA query is loading, or it failed), so
   * the map must not paint either claim. See `computeRouteMonitoringJoin`.
   */
  task_state: 'assigned' | 'active' | 'completed' | 'unknown' | null;
}

export interface RouteLineProperties {
  driver_id: string;
  driver_name: string;
  color: string;
  segment: 'done' | 'pending';
}

export interface DriverLocationMarker {
  workerId: string;
  workerName: string;
  color: string;
  location: [number, number];
  timeLastSeen: number | null;
}

export interface RouteMonitoringJoinResult {
  drivers: MappedDriverRoute[];
  stopsGeoJson: FeatureCollection<Point, EnrichedStopProperties>;
  linesGeoJson: FeatureCollection<LineString, RouteLineProperties>;
  driverLocations: DriverLocationMarker[];
  /**
   * The stops the map paints grey: geocoded fine, but no live driver task
   * claims them (`task_state === null`). They are shaped here, where the join
   * already knows which stops it matched, so the sidebar can list them without
   * rebuilding stops out of GeoJSON properties.
   */
  unassignedStops: RoutePlanningStop[];
}

/** Whole minutes elapsed since a driver's last known ping, never negative. */
export function minutesSinceLastSeen(timeLastSeen: number, now: number = Date.now()): number {
  return Math.max(0, Math.floor((now - timeLastSeen) / 60_000));
}

/** Ascending numeric order that keeps unknown timestamps at the end. */
function byTimeAsc(left: number | null, right: number | null): number {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
}

export function computeRouteMonitoringJoin(input: {
  stops: RoutePlanningStop[];
  etaDrivers: EtaDriver[] | undefined;
  isToday: boolean;
}): RouteMonitoringJoinResult {
  const { stops, etaDrivers, isToday } = input;

  // The Onfleet task id is the single join key between a planned stop and the
  // live task the driver is executing. Stops without one can never be matched.
  // One task id can hold several stops — see `MappedDriverTask.stops` — so this
  // collects them all instead of keeping whichever arrived last.
  const stopsByTaskId = new Map<string, RoutePlanningStop[]>();
  for (const stop of stops) {
    if (!stop.onfleet_task_id) continue;
    const bucket = stopsByTaskId.get(stop.onfleet_task_id);
    if (bucket) bucket.push(stop);
    else stopsByTaskId.set(stop.onfleet_task_id, [stop]);
  }

  // If not today or no drivers, return default unlinked presentation
  if (!isToday || !etaDrivers || etaDrivers.length === 0) {
    // Today with no driver list is the one case where nothing is KNOWN: the
    // ETA query is still loading, or it failed and stays failed. The stops are
    // still drawn — they exist — but they carry no claim about who runs them,
    // the same distinction `unassignedStops` below already makes. A day that
    // is not today keeps the plain grey dot: it has its own notice
    // (`routePlanning.todayOnlyNotice`) saying why there is no live board.
    const awaitingBoard = isToday && etaDrivers === undefined;
    const defaultFeatures: Array<Feature<Point, EnrichedStopProperties>> = stops.map((stop) => ({
      type: 'Feature',
      id: stop.job_id,
      geometry: {
        type: 'Point',
        coordinates: [stop.lng, stop.lat],
      },
      properties: {
        job_id: stop.job_id,
        order_name: stop.order_name,
        zip: stop.zip,
        status: stop.status,
        status_label: lookupEnumLabelOrWarn(
          stop.status,
          jobStatusOptions,
          'route-planning stop status',
        ),
        driver_id: null,
        driver_name: null,
        driver_color: UNASSIGNED_COLOR,
        eta: null,
        completed_at: null,
        task_state: awaitingBoard ? 'unknown' : null,
      },
    }));

    return {
      drivers: [],
      stopsGeoJson: { type: 'FeatureCollection', features: defaultFeatures },
      linesGeoJson: { type: 'FeatureCollection', features: [] },
      driverLocations: [],
      // "Nobody is carrying this stop" is a claim about the live board, so it
      // takes an answer from the board to make. An empty driver list IS such
      // an answer — nobody is on duty, so every stop is unclaimed. `undefined`
      // is not: the ETA query is still loading or it failed, and a day that is
      // not today has no live board to ask. Then the list stays empty and the
      // sidebar group simply does not appear, because "unassigned" is not yet
      // something the code knows.
      unassignedStops: isToday && etaDrivers !== undefined ? stops : [],
    };
  }

  const mappedDrivers: MappedDriverRoute[] = [];
  const lineFeatures: Array<Feature<LineString, RouteLineProperties>> = [];
  const driverLocations: DriverLocationMarker[] = [];
  // Map to store matched details for stops
  const stopMatchedDetails = new Map<
    string,
    {
      driverId: string;
      driverName: string;
      color: string;
      eta: number | null;
      completedAt: number | null;
      taskState: 'assigned' | 'active' | 'completed';
    }
  >();

  etaDrivers.forEach((driver, idx) => {
    const color = DRIVER_COLORS[idx % DRIVER_COLORS.length]!;
    const completedCoords: [number, number][] = [];
    const pendingCoords: [number, number][] = [];

    const rememberMatch = (
      stop: RoutePlanningStop,
      details: {
        eta: number | null;
        completedAt: number | null;
        taskState: 'assigned' | 'active' | 'completed';
      },
    ) => {
      stopMatchedDetails.set(stop.job_id, {
        driverId: driver.workerId,
        driverName: driver.workerName,
        color,
        ...details,
      });
    };

    // Finished stops first, earliest completion first: they draw the track the
    // driver has already covered.
    const completedTasks: MappedDriverTask[] = [...driver.completed]
      .sort((a, b) => byTimeAsc(a.completedAt, b.completedAt))
      .map((task) => {
        const matchedStops = stopsByTaskId.get(task.taskId) ?? [];
        for (const stop of matchedStops) {
          completedCoords.push([stop.lng, stop.lat]);
          rememberMatch(stop, {
            eta: null,
            completedAt: task.completedAt,
            taskState: 'completed',
          });
        }
        return {
          taskId: task.taskId,
          orderName: task.orderName,
          state: 'completed' as const,
          estimatedCompletionTime: null,
          completedAt: task.completedAt,
          stops: matchedStops,
        };
      });

    // Remaining stops in estimated completion order: the route still to run.
    const pendingTasks: MappedDriverTask[] = [...driver.tasks]
      .sort((a, b) => byTimeAsc(a.estimatedCompletionTime, b.estimatedCompletionTime))
      .map((task) => {
        const matchedStops = stopsByTaskId.get(task.taskId) ?? [];
        for (const stop of matchedStops) {
          pendingCoords.push([stop.lng, stop.lat]);
          rememberMatch(stop, {
            eta: task.estimatedCompletionTime,
            completedAt: null,
            taskState: task.state,
          });
        }
        return {
          taskId: task.taskId,
          orderName: task.orderName,
          state: task.state,
          estimatedCompletionTime: task.estimatedCompletionTime,
          completedAt: null,
          stops: matchedStops,
        };
      });

    const location = driver.location ?? null;

    mappedDrivers.push({
      workerId: driver.workerId,
      workerName: driver.workerName,
      color,
      location,
      onDuty: driver.onDuty,
      timeLastSeen: driver.timeLastSeen,
      tasks: [...completedTasks, ...pendingTasks],
      completedCount: driver.completedCount ?? 0,
      coordinates: [...completedCoords, ...pendingCoords],
    });

    if (completedCoords.length >= 2) {
      lineFeatures.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: completedCoords },
        properties: {
          driver_id: driver.workerId,
          driver_name: driver.workerName,
          color,
          segment: 'done',
        },
      });
    }

    // The remaining route hangs off the last delivered stop, or off the driver
    // himself when nothing has been delivered yet — but only while he is on
    // duty. An off-duty driver's last reported GPS is stale and must not be
    // drawn as the start of the leg still to run.
    const lastCompleted = completedCoords[completedCoords.length - 1];
    const pendingStart = lastCompleted ?? (driver.onDuty ? location : null);
    const pendingLineCoords: [number, number][] = pendingStart
      ? [pendingStart, ...pendingCoords]
      : [...pendingCoords];

    if (pendingLineCoords.length >= 2) {
      lineFeatures.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: pendingLineCoords },
        properties: {
          driver_id: driver.workerId,
          driver_name: driver.workerName,
          color,
          segment: 'pending',
        },
      });
    }

    // Only an on-duty driver has a meaningful live position on the map.
    if (driver.onDuty && location) {
      driverLocations.push({
        workerId: driver.workerId,
        workerName: driver.workerName,
        color,
        location: [location[0], location[1]],
        timeLastSeen: driver.timeLastSeen,
      });
    }
  });

  const enrichedStopsFeatures: Array<Feature<Point, EnrichedStopProperties>> = stops.map((stop) => {
    const match = stopMatchedDetails.get(stop.job_id);
    return {
      type: 'Feature',
      id: stop.job_id,
      geometry: {
        type: 'Point',
        coordinates: [stop.lng, stop.lat],
      },
      properties: {
        job_id: stop.job_id,
        order_name: stop.order_name,
        zip: stop.zip,
        status: stop.status,
        status_label: lookupEnumLabelOrWarn(
          stop.status,
          jobStatusOptions,
          'route-planning stop status',
        ),
        driver_id: match ? match.driverId : null,
        driver_name: match ? match.driverName : null,
        driver_color: match ? match.color : UNASSIGNED_COLOR,
        eta: match ? match.eta : null,
        completed_at: match ? match.completedAt : null,
        task_state: match ? match.taskState : null,
      },
    };
  });

  return {
    drivers: mappedDrivers,
    stopsGeoJson: { type: 'FeatureCollection', features: enrichedStopsFeatures },
    linesGeoJson: { type: 'FeatureCollection', features: lineFeatures },
    driverLocations,
    // The same predicate the features above use for `task_state`: no match,
    // no driver.
    unassignedStops: stops.filter((stop) => !stopMatchedDetails.has(stop.job_id)),
  };
}

export function useRouteMonitoringJoin(input: {
  stops: RoutePlanningStop[];
  etaDrivers: EtaDriver[] | undefined;
  isToday: boolean;
}): RouteMonitoringJoinResult {
  return useMemo(
    () => computeRouteMonitoringJoin(input),
    [input.stops, input.etaDrivers, input.isToday],
  );
}
